/**
 * Technology Capability Edit Route
 *
 * Edit an existing technology capability using TechnologyCapabilityForm component
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTechnologyCapability } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import { TechnologyCapabilityForm } from '../../components/technology-capabilities';
import { LoadingSpinner, EmptyState } from '../../components/shared';
import {
  updateTechnologyCapability,
  type UpdateTechnologyCapabilityInput,
  ApiError,
} from '../../lib/apiClient';
import type { CreateTechnologyCapabilityFormData } from '../../components/forms';
import './edit.css';

export const Route = createFileRoute('/technology-capabilities/$technologyCapabilityId/edit')({
  component: TechnologyCapabilityEditPage,
});

function TechnologyCapabilityEditPage(): JSX.Element {
  const BannerElement = 'forge-banner' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const { technologyCapabilityId } = Route.useParams();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch technology capability data
  const { data, loading, error: fetchError } = useTechnologyCapability(technologyCapabilityId);
  const technologyCapability = data?.technologyCapability;

  if (loading) {
    return (
      <div className="technology-capability-edit-page">
        <LoadingSpinner centered message="Loading technology capability..." />
      </div>
    );
  }

  if (fetchError || !technologyCapability) {
    return (
      <div className="technology-capability-edit-page">
        <EmptyState
          icon="error"
          title="Failed to load technology capability"
          description={fetchError?.message || 'Technology capability not found'}
          actionLabel="Back to Technology Capabilities"
          onAction={() =>
            navigate({ to: '/technology-capabilities', search: { page: 1, pageSize: 25 } })
          }
        />
      </div>
    );
  }

  const handleSubmit = async (formData: CreateTechnologyCapabilityFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build update input
      const updates: UpdateTechnologyCapabilityInput = {
        name: formData.name,
        description: formData.description || undefined,
        domainId: formData.domainId || undefined,
        tags: formData.tags && formData.tags.length > 0 ? formData.tags : undefined,
        notes: formData.notes || undefined,
      };

      await updateTechnologyCapability(technologyCapabilityId, updates);

      // Refetch queries to update cache
      await apolloClient.refetchQueries({
        include: ['GetTechnologyCapability', 'ListTechnologyCapabilities'],
      });

      // Navigate back to technology capability detail page
      navigate({ to: `/technology-capabilities/${technologyCapabilityId}` as string });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: `/technology-capabilities/${technologyCapabilityId}` as string });
  };

  // Map technology capability data to form default values
  const defaultValues: Partial<CreateTechnologyCapabilityFormData> = {
    name: technologyCapability.name,
    description: technologyCapability.description || undefined,
    domainId: technologyCapability.domainId || undefined,
    tags: technologyCapability.tags || [],
    notes: technologyCapability.notes || undefined,
  };

  return (
    <div className="technology-capability-edit-page">
      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Edit Technology Capability</h1>
        <p className="forge-typography--body2 page-header-subtitle">
          Update technology capability information
        </p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <TechnologyCapabilityForm
        mode="edit"
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
